import { useCallback, useEffect, useMemo, useState } from 'react';
// MIGRATION: fully on AWS PostgREST.
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { usePolling } from '../lib/poll';
import { formatMoney } from '../lib/money';
import { can } from '../lib/permissions';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import ContactDrawer from './ContactDrawer';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const TYPE_COLORS = { Customer: '#16a34a', Supplier: '#7c3aed', Partner: '#d97706', Contractor: '#f59e0b', Agency: '#ec4899', Both: '#6366f1', Freelancer: '#f59e0b' };

function fmtRelative(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

export default function AddressBook({ session }) {
  // contacts_write gates the "+ New Person" button + the row archive
  // action. Read-only users can still browse + open contact details.
  const canWrite = can(session, 'contacts_write');
  const [contacts, setContacts] = useState([]);
  const [poInvoices, setPoInvoices] = useState([]);
  const [poPayments, setPoPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [drawer, setDrawer] = useState(null);
  const [teamPulse, setTeamPulse] = useState([]);

  const loadContacts = useCallback(async () => {
    const { data } = await aws.from('contacts').select('*').is('archived_at', null).order('company_name');
    setContacts(data || []);
  }, []);

  // Yesterday's Pulse: team activity from yesterday (duty submissions only —
  // tasks + daily_priorities tables were dropped in the 2026-05-08 cleanup).
  const loadTeamPulse = useCallback(async () => {
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().slice(0, 10);
    // Mig 139 (C1, 2026-05-10): staff_directory no longer exposes `code`.
    // Pulse counts query duty_submissions.eq('user_code', s.code) so we
    // need codes — route through the admin RPC. Non-admin sessions get
    // `[]` and the pulse panel renders empty (admin-only feature anyway).
    const { data: staff } = await aws.rpc('admin_staff_directory');
    if (!staff) return;
    const pulseData = [];
    for (const s of staff) {
      const { count: dutiesSubmitted } = await aws.from('duty_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('user_code', s.code)
        .gte('submitted_at', yStr + 'T00:00:00Z')
        .lte('submitted_at', yStr + 'T23:59:59Z');
      const total = dutiesSubmitted || 0;
      if (total > 0) {
        pulseData.push({ code: s.code, name: s.name, tasksCompleted: 0, dutiesSubmitted: total, prisDone: 0, prisTotal: 0, total });
      }
    }
    setTeamPulse(pulseData);
  }, []);

  useEffect(() => {
    loadContacts();
    loadTeamPulse();
    aws.from('po_invoices').select('supplier_name, total').is('archived_at', null).then(({ data }) => setPoInvoices(data || []));
    aws.from('po_invoice_payments').select('amount, invoice_id, po_invoices(supplier_name)').then(({ data }) => setPoPayments(data || []));
  }, [loadContacts, loadTeamPulse]);

  usePolling(loadContacts);

  const financials = useMemo(() => {
    const map = {};
    const get = (name) => { const key = (name || '').toLowerCase().trim(); if (!key) return null; if (!map[key]) map[key] = { invoiced: 0, paid: 0 }; return map[key]; };
    poInvoices.forEach(inv => { const f = get(inv.supplier_name); if (f) f.invoiced += Number(inv.total || 0); });
    poPayments.forEach(p => { const f = get(p.po_invoices?.supplier_name); if (f) f.paid += Number(p.amount || 0); });
    return map;
  }, [poInvoices, poPayments]);

  function getFin(name) { return financials[(name || '').toLowerCase().trim()] || { invoiced: 0, paid: 0 }; }

  // Map category to display type
  const allPeople = useMemo(() =>
    contacts.map(c => ({
      ...c,
      type: c.category === 'Supplier' ? 'Supplier' : c.category === 'Agency' ? 'Agency' : c.category === 'Partner' ? 'Partner' : c.category === 'Freelancer' || c.category === 'Contractor' ? 'Contractor' : c.category === 'Both' ? 'Both' : 'Customer'
    })).sort((a, b) => {
      // Person-first sort: rows with a contact_person come first, alphabetically.
      // Company-only rows sort alphabetically among themselves at the bottom.
      const aKey = (a.contact_person || a.company_name || '').toLowerCase();
      const bKey = (b.contact_person || b.company_name || '').toLowerCase();
      return aKey.localeCompare(bKey);
    })
  , [contacts]);

  // Dynamic type filter counts from real data
  const typeCounts = useMemo(() => {
    const c = { all: 0 };
    allPeople.forEach(p => { c.all++; c[p.type] = (c[p.type] || 0) + 1; });
    return c;
  }, [allPeople]);

  const typeFilters = useMemo(() => {
    const filters = [{ key: 'all', label: 'All' }];
    ['Customer', 'Supplier', 'Partner', 'Contractor', 'Agency', 'Both'].forEach(t => {
      if (typeCounts[t]) filters.push({ key: t, label: t === 'Both' ? 'Both' : t + 's' });
    });
    return filters;
  }, [typeCounts]);

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPeople.filter(c => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      if (q) {
        const hit = (c.company_name || '').toLowerCase().includes(q) || (c.contact_person || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [allPeople, search, typeFilter]);

  function handleDownload() {
    const columns = [
      { label: 'Person', width: 22, value: r => r.contact_person || '' },
      { label: 'Company', width: 24, value: r => r.company_name || '' },
      { label: 'Type', width: 14, value: r => r.type || '' },
      { label: 'Email', width: 26, value: r => r.email || '' },
      { label: 'Invoiced', width: 14, numFmt: '#,##0.00', value: r => getFin(r.company_name).invoiced },
      { label: 'Outstanding', width: 14, numFmt: '#,##0.00', value: r => Math.max(0, getFin(r.company_name).invoiced - getFin(r.company_name).paid) },
      { label: 'Status', width: 12, value: r => r.status || '' },
    ];
    const filename = `FON_Contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Contacts', columns, displayed);
    logExportActivity(session, 'Contacts', filename);
  }

  async function archiveContact(c) {
    if (!confirm(`Archive ${c.contact_person || c.company_name}?`)) return;
    await authWrite(session, 'contacts', 'update',
      { archived_at: new Date().toISOString(), archived_by: session.name },
      { column: 'id', value: c.id });
    loadContacts();
  }

  return (
    <div className="app-screen">
      <div className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#4A4A4A', margin: '0 0 4px' }}>People</h1>
            <p style={{ fontSize: 13, color: '#757575', margin: 0 }}>Every relationship in one place</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="Search people..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '7px 12px', fontSize: 12, border: '1px solid #e4e4e7', borderRadius: 8, width: 200, fontFamily: 'inherit', color: '#4A4A4A' }} />
            {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
            {canWrite && <button className="btn btn-primary btn-sm" onClick={() => setDrawer({ contact: null })} style={{ whiteSpace: 'nowrap' }}>+ New Person</button>}
          </div>
        </div>

        {/* Yesterday's Pulse */}
        {teamPulse.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Yesterday's Pulse</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(teamPulse.length, 4)}, 1fr)`, gap: 12 }}>
              {teamPulse.map(p => {
                const badge = p.total >= 3 && p.prisDone === p.prisTotal ? { label: 'Strong', bg: '#dcfce7', color: '#16a34a' }
                  : p.total > 0 ? { label: 'Steady', bg: '#fef3c7', color: '#d97706' }
                  : { label: 'Light', bg: '#fee2e2', color: '#dc2626' };
                return (
                  <div key={p.code} style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#4A4A4A' }}>{p.name.split(' ')[0].split('(')[0].trim()}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: badge.bg, color: badge.color }}>{badge.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#71717a', lineHeight: 1.6 }}>
                      {p.prisTotal > 0 && <div>Priorities: {p.prisDone}/{p.prisTotal}</div>}
                      {p.tasksCompleted > 0 && <div>Tasks done: {p.tasksCompleted}</div>}
                      {p.dutiesSubmitted > 0 && <div>Reports: {p.dutiesSubmitted}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Type pills */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
          {typeFilters.map(f => (
            <button key={f.key} onClick={() => setTypeFilter(f.key)} style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 500, borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              background: typeFilter === f.key ? '#4A4A4A' : '#f4f4f5',
              color: typeFilter === f.key ? '#fff' : '#52525b',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {f.label}
              <span style={{ fontSize: 10, padding: '0 4px', borderRadius: 999, background: typeFilter === f.key ? 'rgba(255,255,255,0.2)' : '#e4e4e7', color: typeFilter === f.key ? '#fff' : '#757575' }}>{typeCounts[f.key] || 0}</span>
            </button>
          ))}
        </div>

        {/* Table */}
        {displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#757575', fontSize: 13 }}>No people found</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Person', 'Company', 'Type', 'Email', 'Invoiced', 'Outstanding', 'Status', ''].map(h => (
                    <th key={h} style={{ textAlign: h === 'Invoiced' || h === 'Outstanding' ? 'right' : 'left', fontSize: 10, fontWeight: 500, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '12px 16px', borderBottom: '1px solid #f4f4f5', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((c, i) => {
                  const fin = getFin(c.company_name);
                  const outstanding = Math.max(0, fin.invoiced - fin.paid);
                  const typeColor = TYPE_COLORS[c.type] || '#64748b';
                  return (
                    <tr key={c.id || `stub-${i}`} onClick={() => setDrawer({ contact: c })} style={{ cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa', fontWeight: 500, color: '#4A4A4A' }}>{c.contact_person || (c.company_name && !c.contact_person ? c.company_name : '--')}</td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa', color: '#52525b' }}>{c.contact_person && c.company_name ? c.company_name : (c.contact_person ? '--' : '')}</td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: `${typeColor}12`, color: typeColor }}>{c.type}</span>
                      </td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa', color: '#757575', fontSize: 12 }}>{c.email || '--'}</td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: fin.invoiced > 0 ? '#4A4A4A' : '#d4d4d8' }}>{fin.invoiced > 0 ? formatMoney(fin.invoiced) : '--'}</td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontWeight: outstanding > 0 ? 600 : 400, fontVariantNumeric: 'tabular-nums', color: outstanding > 0 ? '#dc2626' : '#d4d4d8' }}>{outstanding > 0 ? formatMoney(outstanding) : '--'}</td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: c.status === 'active' ? '#16a34a' : '#757575' }} />
                          <span style={{ fontSize: 11, color: '#52525b', textTransform: 'capitalize' }}>{c.status}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px', borderBottom: '1px solid #fafafa' }}>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 9 }} onClick={e => { e.stopPropagation(); archiveContact(c); }}>Archive</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawer && <ContactDrawer session={session} contact={drawer.contact} onClose={() => setDrawer(null)} onSaved={loadContacts} />}
    </div>
  );
}
